import { Flex, Icon, Text, useColorModeValue } from '@chakra-ui/react';
import { MdOutlineApartment, MdOutlinePlace } from 'react-icons/md';
import { translate } from '../../i18n';
import { ReadGate, scopePromptI18nKey } from './readState';

/**
 * What a LIST page renders when its read was never issued.
 *
 * Every list surface in this dashboard is gated on a selection the operator
 * may not have — `enabled: Boolean(organizationId)`, `enabled: !!locationId` —
 * and React Query v4 reports a disabled query as `isLoading: true` with
 * `fetchStatus: 'idle'` (measured against the real library in
 * `analytics-read-error-state.test.tsx`, "the premise"). So the six pages this
 * component was written for each drove a loading affordance from the raw flag
 * and spun FOREVER: a whole page reading "Loading…" (`CohortList`), a bare
 * spinner (`CouponList`, `ArticleList`), or a spinner under an empty table
 * header (`CoachList`, `EventList`, `UserList`).
 *
 * The reason this is a component and not a one-line flag swap is what the swap
 * would have produced. Routing those same surfaces through `isInitialLoading`
 * alone stops the spinner and drops each page into the arm BELOW it — which on
 * three of the six is a definitive claim about the organization made from a
 * question nobody asked ("No coupons yet", `no_data`, "No events yet — create
 * your first"), and on the other three is a silently empty table. Run 101 saw
 * the same trap on the navbar bell. The honest fourth state needs its own
 * render, and this is it.
 *
 * Deliberately NOT `EmptyState`: that component is the dashboard's vocabulary
 * for "we asked and there is nothing", which is the sentence this state exists
 * to stop. It follows `DESIGN.md`'s no-results shape instead (48px muted icon,
 * 16px Urbanist semibold headline, no CTA) — no CTA because retrying or
 * creating changes nothing until a scope is chosen, and the control that does
 * is the picker in the navbar, not anything on this page.
 *
 * The string is a BORROWED i18n key, not a new one: `src/i18n/{en,fr}.ts` sit
 * inside the open-PR fence (R96-7). `scopePromptI18nKey` owns the choice so
 * this component and `ChartReadState` cannot drift apart, and un-borrowing is
 * one edit in `readState.ts` when the fence lifts (R100-8 / R102-3).
 */
export default function ScopePrompt({ gate }: { gate: ReadGate }) {
    const iconColor = useColorModeValue('secondaryGray.500', 'secondaryGray.600');
    const titleColor = useColorModeValue('secondaryGray.900', 'white');

    return (
        <Flex direction='column' align='center' justify='center' py='40px' px='20px'>
            <Icon
                as={gate === 'location' ? MdOutlinePlace : MdOutlineApartment}
                w='48px'
                h='48px'
                color={iconColor}
                mb='16px'
            />
            <Text
                color={titleColor}
                fontSize='md'
                fontWeight='600'
                textAlign='center'
                maxW='360px'
            >
                {translate(scopePromptI18nKey(gate) as any)}
            </Text>
        </Flex>
    );
}
